import React, { useState, useEffect } from "react";
import { FaEdit } from "react-icons/fa";
import { API_URL, UserTypes } from "../constants";
import axios from "axios";

const CapteurThresholds = ({ capteurId, token }) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const userType = user.type;

  const [onThreshold, setOnThreshold] = useState(null);
  const [offThreshold, setOffThreshold] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [newOnThreshold, setNewOnThreshold] = useState("");
  const [newOffThreshold, setNewOffThreshold] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchThresholds = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/capteurs/${capteurId}/thresholds`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setOnThreshold(response.data.on_threshold);
        setOffThreshold(response.data.off_threshold);
        setNewOnThreshold(response.data.on_threshold);
        setNewOffThreshold(response.data.off_threshold);
      } catch (err) {
        console.error("Erreur lors de la récupération des seuils :", err.message);
      } finally {
        setLoading(false);
      }
    };

    if (capteurId) {
      fetchThresholds();
    }
  }, [capteurId, token]);

  const handleSaveClick = async () => {
    setError(null);

    const on = parseFloat(newOnThreshold);
    const off = parseFloat(newOffThreshold);

    if (isNaN(on) || isNaN(off)) {
      setError("Les seuils doivent être des nombres valides.");
      return;
    }

    if (off >= on) {
      setError("Le seuil d'arrêt (OFF) doit être strictement inférieur au seuil de démarrage (ON).");
      return;
    }

    try {
      await axios.patch(
        `${API_URL}/capteurs/${capteurId}/thresholds`,
        { on_threshold: on, off_threshold: off },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setOnThreshold(on);
      setOffThreshold(off);
      setEditMode(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      const msg = err.response?.data?.message || "Erreur lors de la mise à jour des seuils.";
      setError(msg);
    }
  };

  const handleCancelClick = () => {
    setNewOnThreshold(onThreshold);
    setNewOffThreshold(offThreshold);
    setEditMode(false);
    setError(null);
  };

  if (userType !== UserTypes.SUPER) return null;
  if (loading) return null;

  return (
    <div className="pt-4 pb-4 text-xl 3xl:text-3xl">
      {editMode ? (
        <div className="flex flex-col gap-4 mt-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold underline text-xs lg:text-xl 3xl:text-3xl">
              Seuil démarrage (ON) :
            </span>
            <input
              type="number"
              step="0.1"
              value={newOnThreshold}
              onChange={(e) => setNewOnThreshold(e.target.value)}
              className="border-2 border-gray-300 rounded-md p-2 w-24 text-xs lg:text-xl"
            />
            <span className="text-xs lg:text-xl">A</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold underline text-xs lg:text-xl 3xl:text-3xl">
              Seuil arrêt (OFF) :
            </span>
            <input
              type="number"
              step="0.1"
              value={newOffThreshold}
              onChange={(e) => setNewOffThreshold(e.target.value)}
              className="border-2 border-gray-300 rounded-md p-2 w-24 text-xs lg:text-xl"
            />
            <span className="text-xs lg:text-xl">A</span>
          </div>
          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleSaveClick}
              className="bg-loginBordure hover:bg-blue-900 text-white rounded-md px-4 py-2 text-xs lg:text-xl"
            >
              Modifier
            </button>
            <button
              onClick={handleCancelClick}
              className="bg-red-500 hover:bg-red-700 text-white rounded-md px-4 py-2 text-xs lg:text-xl"
            >
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <span className="inline-flex items-center font-light">
            <span className="font-bold underline mr-2 text-xs lg:text-xl 3xl:text-3xl">
              Seuil démarrage (ON) :
            </span>
            <span className="text-xs lg:text-xl 3xl:text-3xl">{onThreshold} A</span>
          </span>
          <span className="inline-flex items-center font-light">
            <span className="font-bold underline mr-2 text-xs lg:text-xl 3xl:text-3xl">
              Seuil arrêt (OFF) :
            </span>
            <span className="text-xs lg:text-xl 3xl:text-3xl">{offThreshold} A</span>
            <FaEdit
              className="cursor-pointer ml-4 hover:text-blue-500 transition-all duration-200 ease-in-out"
              onClick={() => setEditMode(true)}
            />
          </span>
          {success && (
            <p className="text-green-600 text-sm font-semibold">Seuils mis à jour avec succès.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CapteurThresholds;